import type { ReactNode } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { PortalModule } from '@ciimar/shared';
import { useAuth } from './auth/AuthContext';
import { AccessProvider, useAccess } from './auth/AccessContext';
import { UnifiedShell } from './portal/UnifiedShell';
import { Dashboard } from './pages/Dashboard';
import { IamNew } from './pages/IamNew';
import { Login } from './pages/Login';
import { MyProfile } from './pages/MyProfile';
import { MyRequests } from './pages/MyRequests';
import { MyTeam } from './pages/MyTeam';
import { ModulePlaceholder } from './pages/ModulePlaceholder';
import { NewRegistration } from './pages/NewRegistration';
import { OnboardingLanding } from './pages/OnboardingLanding';
import { ActiveDirectory } from './pages/ActiveDirectory';
import { DeletedPeople } from './pages/DeletedPeople';
import { People } from './pages/People';
import { Permissions } from './pages/Permissions';
import { PersonRecord } from './pages/PersonRecord';
import { SettingsDashboard, SettingsFields, SettingsSistrade } from './pages/Settings';
import { SettingsGlobal } from './pages/SettingsGlobal';
import { SettingsIt } from './pages/SettingsIt';
import { SubmissionDetail } from './pages/SubmissionDetail';
import { Submissions } from './pages/Submissions';
import { Teams } from './pages/Teams';
import { Articles } from './pages/Articles';
import { Attendance } from './pages/Attendance';
import { Vehicles } from './pages/Vehicles';

/** The friendly public entry point (onboarding.ciimar.up.pt) shows a code form. */
const isOnboardingHost =
  typeof window !== 'undefined' && window.location.hostname.startsWith('onboarding.');

/** Gate a route by module access; admins pass everything. Falls back to Meu Perfil. */
function RequireModule({ module, children }: { module: PortalModule; children: ReactNode }) {
  const { has, loading } = useAccess();
  if (loading) return <div className="centered">A carregar…</div>;
  return has(module) ? <>{children}</> : <Navigate to="/me" replace />;
}

/** Landing: managers see the dashboard, researchers their team, others their profile. */
function Home() {
  const { isManager, isResearcher } = useAuth();
  if (isManager) return <Dashboard />;
  if (isResearcher) return <MyTeam />;
  return <Navigate to="/me" replace />;
}

/**
 * Authenticated layout: requires a signed-in user, resolves module access once
 * and frames every page in the unified Apple-style shell.
 */
function AuthedLayout() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="centered">A carregar…</div>;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return (
    <AccessProvider>
      <UnifiedShell>
        <Outlet />
      </UnifiedShell>
    </AccessProvider>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/iamnew" element={<IamNew />} />
      <Route path="/login" element={<Login />} />
      {/* Legacy standalone routes folded into the unified app. */}
      <Route path="/portal" element={<Navigate to="/" replace />} />
      <Route path="/portal/login" element={<Navigate to="/login" replace />} />
      <Route path="/onboarding-v2" element={<Navigate to="/" replace />} />

      {/* The public onboarding host serves the code form at the root. */}
      {isOnboardingHost && <Route path="/" element={<OnboardingLanding />} />}

      <Route element={<AuthedLayout />}>
        {!isOnboardingHost && <Route index element={<Home />} />}
        <Route path="me" element={<MyProfile />} />

        <Route
          path="people"
          element={
            <RequireModule module={PortalModule.Colaboradores}>
              <People />
            </RequireModule>
          }
        />
        <Route
          path="people/eliminados"
          element={
            <RequireModule module={PortalModule.Colaboradores}>
              <DeletedPeople />
            </RequireModule>
          }
        />
        <Route
          path="people/:id"
          element={
            <RequireModule module={PortalModule.Colaboradores}>
              <PersonRecord />
            </RequireModule>
          }
        />

        <Route
          path="submissions"
          element={
            <RequireModule module={PortalModule.Onboarding}>
              <Submissions />
            </RequireModule>
          }
        />
        <Route
          path="submissions/:id"
          element={
            <RequireModule module={PortalModule.Onboarding}>
              <SubmissionDetail />
            </RequireModule>
          }
        />
        <Route
          path="requests"
          element={
            <RequireModule module={PortalModule.Onboarding}>
              <MyRequests />
            </RequireModule>
          }
        />
        <Route
          path="new"
          element={
            <RequireModule module={PortalModule.Onboarding}>
              <NewRegistration />
            </RequireModule>
          }
        />

        <Route
          path="viaturas"
          element={
            <RequireModule module={PortalModule.Viaturas}>
              <Vehicles />
            </RequireModule>
          }
        />
        <Route
          path="equipa"
          element={
            <RequireModule module={PortalModule.Equipa}>
              <Teams />
            </RequireModule>
          }
        />
        <Route
          path="artigos"
          element={
            <RequireModule module={PortalModule.Artigos}>
              <Articles />
            </RequireModule>
          }
        />
        <Route
          path="presencas"
          element={
            <RequireModule module={PortalModule.Presencas}>
              <Attendance />
            </RequireModule>
          }
        />

        <Route
          path="rh"
          element={
            <RequireModule module={PortalModule.Rh}>
              <ModulePlaceholder module={PortalModule.Rh} />
            </RequireModule>
          }
        />
        <Route
          path="safelab"
          element={
            <RequireModule module={PortalModule.SafeLab}>
              <ModulePlaceholder module={PortalModule.SafeLab} />
            </RequireModule>
          }
        />

        <Route
          path="settings"
          element={
            <RequireModule module={PortalModule.Definicoes}>
              <Navigate to="/settings/global" replace />
            </RequireModule>
          }
        />
        <Route
          path="settings/global"
          element={
            <RequireModule module={PortalModule.Definicoes}>
              <SettingsGlobal />
            </RequireModule>
          }
        />
        <Route
          path="settings/campos"
          element={
            <RequireModule module={PortalModule.Definicoes}>
              <SettingsFields />
            </RequireModule>
          }
        />
        <Route
          path="settings/painel"
          element={
            <RequireModule module={PortalModule.Definicoes}>
              <SettingsDashboard />
            </RequireModule>
          }
        />
        <Route
          path="settings/integracao"
          element={
            <RequireModule module={PortalModule.Definicoes}>
              <SettingsSistrade />
            </RequireModule>
          }
        />
        <Route
          path="permissions"
          element={
            <RequireModule module={PortalModule.Permissoes}>
              <Permissions />
            </RequireModule>
          }
        />
        <Route
          path="ad"
          element={
            <RequireModule module={PortalModule.ActiveDirectory}>
              <ActiveDirectory />
            </RequireModule>
          }
        />
        {/* IT administration self-gates on isAdminIt (impersonation + list). */}
        <Route path="ti" element={<SettingsIt />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
